import React, { useState } from "react";
import { motion } from "framer-motion";

import {
  Navbar,
  Footer,
  FloatingActions,
  ContactForm,
  SocialRow,
} from "../components/Shared";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

const brands = [
  "Vertiv", "Hubbell", "RACO", "Burndy", "Corning", "Appleton",
  "Calmint", "Honeywell", "Eaton", "Pizzato", "ABB", "Siemens",
  "Legrand", "APC", "Schneider", "Belden", "Omron", "Moxa",
];

const categories = [
  {
    icon: "fa-microchip",
    title: "Industrial Electrical & Electronic Components",
    items: ["Relays & Sensors", "PLC Components", "Industrial Controllers", "Automation Accessories", "Electronic Components"],
  },
  {
    icon: "fa-plug",
    title: "Electrical & Network Products",
    items: ["Plugs & Connectors", "Switches & Receptacles", "Patch Panels", "CAT6 UTP Cable", "RJ45 Keystone Jacks"],
  },
  {
    icon: "fa-lightbulb",
    title: "Lighting & Signaling",
    items: ["Signal Lights", "Warning Systems", "Industrial Indicators", "Motor Sirens", "Emergency Lighting"],
  },
  {
    icon: "fa-network-wired",
    title: "Telecom & Connectivity",
    items: ["Structured Cabling", "LMR-400 Cables", "TNC Connectors", "Connectivity Systems", "Network Accessories"],
  },
  {
    icon: "fa-battery-full",
    title: "Power Solutions",
    items: ["UPS Systems", "Rack PDUs", "Power Backup", "Monitoring Systems", "Power Infrastructure"],
  },
  {
    icon: "fa-industry",
    title: "Industrial Supply",
    items: ["Global Brand Supply", "Fast Procurement", "Project Support", "Industrial Accessories", "Infrastructure Materials"],
  },
];

export default function Trading() {
  const [openCard, setOpenCard] = useState(null);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden text-slate-800">

      {/* NAVBAR */}
      <div className="fixed top-0 left-0 w-full z-[100] backdrop-blur-xl">
        <Navbar showProducts={true} showVertiv={true} />
      </div>

      {/* ========================= */}
      {/* HERO */}
      {/* ========================= */}
      <section
        id="home"
        className="relative overflow-hidden min-h-[100svh] sm:min-h-screen bg-cover bg-center pt-20 flex items-center"
        style={{ backgroundImage: "url('/trading-aboutus.png')" }}
      >
        <div className="absolute inset-0 bg-white/72" />
        <div className="absolute inset-0 bg-gradient-to-b sm:bg-gradient-to-r from-white via-white/80 to-blue-100/40" />
        <div className="absolute top-[-120px] right-[-100px] w-[400px] h-[400px] bg-cyan-300/20 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-[1450px] mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-12 lg:py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-14 items-center">

            {/* LEFT CONTENT */}
            <motion.div
              className="max-w-2xl"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              {/* SMALL TAG */}
              <motion.div
                variants={fadeUp}
                className="mt-4 sm:mt-8 inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-2.5 sm:py-3 rounded-full bg-white border border-blue-100 shadow-md"
              >
                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-r from-blue-700 to-cyan-500 text-white grid place-items-center flex-shrink-0">
                  <i className="fas fa-bolt text-xs sm:text-sm" />
                </div>
                <span className="text-xs sm:text-sm font-semibold text-blue-800 tracking-wide">
                  Industrial Trading & Supply • UAE
                </span>
              </motion.div>

              {/* HEADING */}
              <motion.h1
                variants={fadeUp}
                className="mt-5 sm:mt-8 text-[34px] sm:text-[46px] md:text-[62px] lg:text-[74px] font-black leading-[1] tracking-[-2px] sm:tracking-[-3px] text-slate-900"
              >
                Powering
                <span className="block bg-gradient-to-r from-blue-700 via-cyan-500 to-blue-500 bg-clip-text text-transparent">
                  Industrial Growth
                </span>
                <span className="block text-slate-800">With Trusted Brands</span>
              </motion.h1>

              {/* DESCRIPTION */}
              <motion.p
                variants={fadeUp}
                className="mt-5 sm:mt-7 text-sm sm:text-[16px] md:text-[18px] text-slate-600 leading-[1.9] max-w-2xl"
              >
                BAJABAR INCORPORATION FZE delivers world-class electrical, telecom, lighting, automation and industrial power solutions from globally trusted brands for infrastructure and commercial projects.
              </motion.p>

              {/* BUTTONS */}
              <motion.div variants={fadeUp} className="mt-7 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-5">
                <a
                  href="#contact"
                  className="group relative overflow-hidden px-6 sm:px-7 py-3.5 sm:py-4 rounded-2xl bg-gradient-to-r from-blue-700 to-cyan-500 text-white font-bold shadow-[0_15px_40px_rgba(37,99,235,0.35)] active:scale-95 hover:scale-105 transition-all duration-500 text-sm md:text-base text-center"
                >
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-all duration-500" />
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <i className="fas fa-paper-plane" />
                    Send Inquiry
                  </span>
                </a>
                <a
                  href="#products"
                  className="px-6 sm:px-7 py-3.5 sm:py-4 rounded-2xl border border-blue-200 bg-white text-blue-700 font-bold active:scale-95 hover:bg-blue-50 hover:scale-105 transition-all duration-500 shadow-lg text-sm md:text-base text-center"
                >
                  <i className="fas fa-boxes mr-2" />
                  Explore Products
                </a>
              </motion.div>

              {/* FEATURES */}
              <motion.div variants={fadeUp} className="mt-7 sm:mt-10 flex flex-wrap gap-3 sm:gap-6">
                {[["fa-award", "Authorized Supplier"], ["fa-globe", "Worldwide Distribution"], ["fa-shield-alt", "Trusted Quality"]].map(([icon, text]) => (
                  <div key={text} className="flex items-center gap-2 sm:gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-100 text-blue-700 grid place-items-center">
                      <i className={`fas ${icon} text-sm`} />
                    </div>
                    <span className="font-semibold text-slate-700 text-xs sm:text-sm">{text}</span>
                  </div>
                ))}
              </motion.div>

              {/* STATS */}
              <motion.div variants={stagger} className="mt-10 sm:mt-14 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl">
                {[["100+", "Global Brands"], ["500+", "Industrial Products"], ["24/7", "Support Service"]].map(([num, label]) => (
                  <motion.div
                    key={label}
                    variants={fadeUp}
                    className="bg-white border border-blue-100 rounded-2xl sm:rounded-3xl px-3 sm:px-5 py-4 sm:py-6 shadow-xl active:scale-95 transition-transform"
                  >
                    <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                      {num}
                    </div>
                    <div className="mt-2 sm:mt-3 uppercase tracking-[0.15em] sm:tracking-[0.22em] text-[9px] sm:text-[11px] font-bold text-slate-500">
                      {label}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* RIGHT SIDE CARDS */}
            <motion.div
              className="hidden sm:grid grid-cols-2 gap-4 sm:gap-6"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              {[
                { i: "fa-microchip", t: "Automation", d: "Industrial Controllers & PLC" },
                { i: "fa-plug", t: "Electrical", d: "Plugs, Switches & Connectors" },
                { i: "fa-lightbulb", t: "Lighting", d: "Industrial Signaling Systems" },
                { i: "fa-network-wired", t: "Telecom", d: "Connectivity & Cabling" },
                { i: "fa-battery-full", t: "Power", d: "UPS & Rack Solutions" },
                { i: "fa-industry", t: "Supply", d: "Global Industrial Procurement" },
              ].map((c) => (
                <motion.div
                  key={c.t}
                  variants={fadeUp}
                  className="group bg-white/90 backdrop-blur-xl border border-blue-100 rounded-[24px] sm:rounded-[30px] p-4 sm:p-6 shadow-[0_15px_40px_rgba(0,0,0,0.08)] hover:-translate-y-2 hover:shadow-[0_25px_60px_rgba(37,99,235,0.18)] active:scale-95 transition-all duration-500"
                >
                  <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white grid place-items-center text-base sm:text-xl shadow-lg group-hover:scale-110 transition-all duration-500">
                    <i className={`fas ${c.i}`} />
                  </div>
                  <h3 className="mt-4 sm:mt-5 text-base sm:text-xl font-bold text-slate-900">{c.t}</h3>
                  <p className="mt-2 sm:mt-3 text-slate-600 leading-relaxed text-xs sm:text-sm">{c.d}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* Mobile: horizontal scroll category pills */}
            <motion.div
              className="sm:hidden flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              {[
                { i: "fa-microchip", t: "Automation" },
                { i: "fa-plug", t: "Electrical" },
                { i: "fa-lightbulb", t: "Lighting" },
                { i: "fa-network-wired", t: "Telecom" },
                { i: "fa-battery-full", t: "Power" },
                { i: "fa-industry", t: "Supply" },
              ].map((c) => (
                <div key={c.t} className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-white/90 backdrop-blur-xl rounded-2xl border border-blue-100 shadow-md">
                  <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white grid place-items-center text-xs flex-shrink-0">
                    <i className={`fas ${c.i}`} />
                  </div>
                  <span className="text-slate-800 font-bold text-xs whitespace-nowrap">{c.t}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white to-transparent" />
      </section>

      {/* ========================= */}
      {/* ABOUT */}
      {/* ========================= */}
      <section id="about" className="max-w-[1450px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
        <motion.div
          className="text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.span
            variants={fadeUp}
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-[0.18em] text-xs"
          >
            About Us
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="mt-5 sm:mt-7 text-[28px] sm:text-[38px] md:text-[56px] lg:text-[68px] font-black leading-[1.05] tracking-[-2px] sm:tracking-[-3px] text-slate-900"
          >
            Reliable Industrial
            <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
              Trading Solutions
            </span>
          </motion.h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center mt-12 sm:mt-20">
          {/* LEFT */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} className="text-slate-600 text-sm sm:text-base md:text-lg leading-[2]">
              BAJABAR INCORPORATION FZE is a leading supplier of electrical, industrial, telecom and lighting solutions serving infrastructure and commercial sectors.
            </motion.p>
            <motion.p variants={fadeUp} className="mt-4 sm:mt-6 text-slate-600 text-sm sm:text-base md:text-lg leading-[2]">
              We provide reliable procurement, fast delivery, competitive pricing and high-quality materials sourced from globally trusted manufacturers.
            </motion.p>

            <motion.div variants={stagger} className="mt-7 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5">
              {["Industrial Electrical Supply", "Telecom & Connectivity", "Global Vendor Network", "Fast Procurement"].map((item) => (
                <motion.div
                  key={item}
                  variants={fadeUp}
                  className="flex items-center gap-3 sm:gap-4 bg-white rounded-2xl sm:rounded-3xl border border-blue-100 p-4 sm:p-5 shadow-lg active:scale-95 transition-transform"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-blue-100 text-blue-700 grid place-items-center flex-shrink-0">
                    <i className="fas fa-check" />
                  </div>
                  <div className="font-semibold text-slate-700 text-sm sm:text-base">{item}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* IMAGE */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="rounded-[28px] sm:rounded-[40px] overflow-hidden border border-blue-100 shadow-[0_25px_60px_rgba(0,0,0,0.12)]">
              <img
                loading="lazy"
                src="/trading-aboutus.png"
                alt="Industrial"
                className="w-full h-[280px] sm:h-[420px] md:h-[550px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08142b]/55 via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========================= */}
      {/* BRANDS */}
      {/* ========================= */}
      <section id="brands" className="py-14 sm:py-20 bg-blue-50/60">
        <div className="max-w-[1450px] mx-auto px-4 sm:px-6 lg:px-10">
          <motion.div
            className="text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.span variants={fadeUp} className="bj-eyebrow">Trusted Global Partners</motion.span>
            <motion.h2 variants={fadeUp} className="mt-3 sm:mt-4 text-[26px] sm:text-[36px] md:text-[52px] font-black text-blue-950">
              Brands We Supply
            </motion.h2>
          </motion.div>

          <motion.div
            className="mt-10 sm:mt-14 grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-5"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
          >
            {brands.map((b, i) => (
              <motion.div
                key={`${b}-${i}`}
                variants={fadeUp}
                className="bg-white rounded-xl sm:rounded-2xl border border-blue-100 py-3 sm:py-5 text-center font-bold text-blue-800 shadow-md hover:shadow-xl hover:-translate-y-1 active:scale-95 transition-all duration-300 text-xs sm:text-sm"
              >
                {b}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ========================= */}
      {/* PRODUCTS */}
      {/* ========================= */}
      <section id="products" className="max-w-[1450px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
        <motion.div
          className="text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.span
            variants={fadeUp}
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-[0.18em] text-xs"
          >
            Product Categories
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="mt-5 sm:mt-7 text-[28px] sm:text-[38px] md:text-[56px] lg:text-[68px] font-black leading-[1.05] tracking-[-2px] sm:tracking-[-3px] text-slate-900"
          >
            Complete Industrial
            <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
              Product Solutions
            </span>
          </motion.h2>
        </motion.div>

        <motion.div
          className="mt-10 sm:mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={stagger}
        >
          {categories.map((c) => (
            <motion.div
              key={c.title}
              variants={fadeUp}
              className="group bg-white border border-blue-100 rounded-[24px] sm:rounded-[34px] p-5 sm:p-7 shadow-[0_15px_40px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-[0_25px_60px_rgba(37,99,235,0.16)] active:scale-[0.98] transition-all duration-500"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white grid place-items-center text-lg sm:text-2xl shadow-xl">
                <i className={`fas ${c.icon}`} />
              </div>
              <h3 className="mt-4 sm:mt-6 text-lg sm:text-2xl font-bold text-slate-900 leading-snug">{c.title}</h3>
              <div className="w-12 sm:w-16 h-1 rounded-full bg-gradient-to-r from-blue-700 to-cyan-500 mt-4 sm:mt-5" />
              <ul className="mt-5 sm:mt-7 space-y-3 sm:space-y-4">
                {c.items.map((it, i) => (
                  <li key={`${it}-${i}`} className="flex items-start gap-3 sm:gap-4 text-slate-600 leading-relaxed text-sm sm:text-base">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-100 text-blue-700 grid place-items-center text-[9px] sm:text-[10px] mt-0.5 flex-shrink-0">
                      <i className="fas fa-check" />
                    </div>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ========================= */}
      {/* VERTIV */}
      {/* ========================= */}
      <section className="bg-gradient-to-b from-blue-50/70 to-white py-16 sm:py-20 lg:py-24" id="vertiv">
        <div className="max-w-[1450px] mx-auto px-4 sm:px-6 lg:px-10">

          {/* HEADER */}
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-[0.18em] text-xs"
            >
              Vertiv Solutions
            </motion.span>
            <motion.h2
              variants={fadeUp}
              className="mt-5 sm:mt-7 text-[28px] sm:text-[38px] md:text-[56px] lg:text-[68px] font-black leading-[1.05] tracking-[-2px] sm:tracking-[-3px] text-slate-900"
            >
              Vertiv Silver
              <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                Solution Provider
              </span>
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-5 sm:mt-7 text-slate-600 text-sm sm:text-base md:text-lg leading-[1.9] max-w-3xl mx-auto">
              Critical infrastructure solutions for data centers, communication networks and industrial environments. BAJABAR INCORPORATION FZE supplies reliable Vertiv power, rack and monitoring solutions for mission-critical operations.
            </motion.p>
          </motion.div>

          {/* GRID */}
          <motion.div
            className="mt-10 sm:mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
          >
            {[
              {
                icon: "fa-battery-full",
                title: "UPS Solutions",
                desc: "Reliable backup power infrastructure for mission critical environments.",
                features: ["Data Centers", "Server Rooms", "Remote Monitoring", "Energy Efficient"],
                more: {
                  applications: ["Industrial Facilities", "Emergency Lighting", "Communication Networks"],
                  capacity: "0.35 kVA to 500 kVA",
                  compliance: ["Energy Star", "CE Certified", "TAA Compliant"],
                  monitoring: ["Communication Cards", "Battery Monitoring", "Remote Access"],
                },
              },
              {
                icon: "fa-server",
                title: "Racks & Containment",
                desc: "Structured rack infrastructure and airflow optimization systems.",
                features: ["Server Cabinets", "Cable Management", "Cooling Efficiency", "Scalable Deployment"],
                more: {
                  products: ["Wall Mount Racks", "Open Frame Racks", "Industrial Rack Systems"],
                  sizes: "18U, 24U, 42U & Custom Sizes",
                  deployment: ["Structured Cabling", "Data Center Ready", "Heavy Duty Capacity"],
                },
              },
              {
                icon: "fa-plug",
                title: "Rack PDUs",
                desc: "Intelligent rack power monitoring and energy management systems.",
                features: ["Smart PDUs", "Remote Access", "Power Analytics", "Monitoring Systems"],
                more: {
                  pdus: ["Basic Rack PDUs", "Metered PDUs", "Switched PDUs"],
                  solutions: ["Environmental Sensors", "Cable Management", "Monitoring Software"],
                },
              },
            ].map((v, index) => (
              <motion.div
                key={v.title}
                variants={fadeUp}
                className="bg-white rounded-[24px] sm:rounded-[30px] border border-blue-100 overflow-hidden shadow-[0_15px_45px_rgba(0,0,0,0.07)] hover:-translate-y-1 active:scale-[0.98] transition-all duration-500"
              >
                {/* TOP */}
                <div className="bg-gradient-to-r from-blue-700 to-cyan-500 text-white p-5 sm:p-6">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-xl sm:rounded-2xl bg-white/15 grid place-items-center text-lg sm:text-xl lg:text-2xl">
                    <i className={`fas ${v.icon}`} />
                  </div>
                  <h3 className="font-display text-lg sm:text-xl lg:text-2xl font-bold mt-4 sm:mt-5">{v.title}</h3>
                  <p className="text-blue-100 text-xs sm:text-[13px] md:text-sm mt-2 sm:mt-3 leading-relaxed">{v.desc}</p>
                </div>

                {/* CONTENT */}
                <div className="p-5 sm:p-6">
                  <ul className="space-y-2 sm:space-y-3">
                    {v.features.map((item) => (
                      <li key={item} className="flex items-center gap-2 sm:gap-3 text-slate-600 text-xs sm:text-[13px] md:text-sm">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-100 text-blue-700 grid place-items-center text-[9px] sm:text-[10px] flex-shrink-0">
                          <i className="fas fa-check" />
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-4 sm:mt-6 text-xs sm:text-sm text-slate-500 leading-relaxed">
                    Explore additional technical specifications, monitoring capabilities and infrastructure solutions.
                  </div>

                  <button
                    onClick={() => setOpenCard(openCard === v.title ? null : v.title)}
                    className="mt-4 sm:mt-6 inline-flex items-center gap-2 text-blue-700 font-bold hover:text-cyan-600 active:scale-95 transition text-sm sm:text-base"
                  >
                    {openCard === v.title ? "Show Less" : "Read More"}
                    <i className={`fas ${openCard === v.title ? "fa-chevron-up" : "fa-chevron-down"} text-xs sm:text-sm`} />
                  </button>

                  {/* EXPANDED */}
                  <div
                    className={`transition-all duration-500 overflow-hidden ${
                      openCard === v.title ? "max-h-[600px] opacity-100 mt-5 sm:mt-7" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="border-t border-blue-100 pt-4 sm:pt-6 space-y-4 sm:space-y-6">
                      {v.more.capacity && (
                        <div>
                          <h4 className="font-bold text-blue-800 text-xs sm:text-sm uppercase tracking-[0.15em]">Capacity Range</h4>
                          <p className="mt-2 sm:mt-3 text-slate-600 text-xs sm:text-sm">{v.more.capacity}</p>
                        </div>
                      )}
                      {v.more.sizes && (
                        <div>
                          <h4 className="font-bold text-blue-800 text-xs sm:text-sm uppercase tracking-[0.15em]">Rack Sizes</h4>
                          <p className="mt-2 sm:mt-3 text-slate-600 text-xs sm:text-sm">{v.more.sizes}</p>
                        </div>
                      )}
                      {Object.entries(v.more).map(([key, value]) => {
                        if (typeof value === "string") return null;
                        return (
                          <div key={key}>
                            <h4 className="font-bold text-blue-800 text-xs sm:text-sm uppercase tracking-[0.15em]">{key}</h4>
                            <ul className="mt-2 sm:mt-3 space-y-1.5 sm:space-y-2">
                              {value.map((x, i) => (
                                <li key={`${x}-${i}`} className="flex items-start gap-2 sm:gap-3 text-slate-600 text-xs sm:text-sm">
                                  <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-blue-100 text-blue-700 grid place-items-center text-[8px] sm:text-[9px] mt-[2px] flex-shrink-0">
                                    <i className="fas fa-check" />
                                  </div>
                                  {x}
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* AUTHORIZED BRANDS BANNER */}
          <motion.div
            className="mt-12 sm:mt-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-gradient-to-r from-blue-700 via-blue-800 to-cyan-600 rounded-[28px] sm:rounded-[36px] px-6 sm:px-8 py-8 sm:py-10 text-center text-white shadow-[0_25px_70px_rgba(0,0,0,0.12)]">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto rounded-2xl sm:rounded-3xl bg-white/15 backdrop-blur-xl grid place-items-center text-xl sm:text-2xl">
                <i className="fas fa-award" />
              </div>
              <h3 className="mt-4 sm:mt-6 text-2xl sm:text-3xl md:text-4xl font-black">Authorized Supply Network</h3>
              <p className="mt-3 sm:mt-5 text-blue-100 max-w-3xl mx-auto leading-[1.9] text-sm sm:text-base">
                BAJABAR INCORPORATION FZE supplies infrastructure solutions and industrial products from globally trusted manufacturers including Vertiv, Hubbell, RACO, Schneider Electric, APC, Eaton and other leading brands.
              </p>
              <div className="mt-7 sm:mt-10 flex flex-wrap justify-center gap-2 sm:gap-4">
                {["Vertiv", "Hubbell", "RACO", "Schneider", "APC", "Eaton", "ABB", "Siemens"].map((brand) => (
                  <div
                    key={brand}
                    className="px-3 sm:px-5 py-2 sm:py-3 rounded-xl sm:rounded-2xl bg-white/10 border border-white/10 backdrop-blur-xl text-xs sm:text-sm font-bold tracking-wide"
                  >
                    {brand}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========================= */}
      {/* WHY US */}
      {/* ========================= */}
      <section id="why" className="relative py-16 sm:py-24 overflow-hidden bg-gradient-to-b from-white to-blue-50">
        <div className="absolute top-[-150px] left-[-120px] w-[420px] h-[420px] bg-blue-200/20 rounded-full blur-[70px] pointer-events-none" />
        <div className="absolute bottom-[-150px] right-[-120px] w-[420px] h-[420px] bg-cyan-200/20 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-[1450px] mx-auto px-4 sm:px-6 lg:px-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-blue-50 border border-blue-100 text-blue-700 font-bold uppercase tracking-[0.18em] text-xs"
            >
              Why Choose Bajabar
            </motion.span>
            <motion.h2
              variants={fadeUp}
              className="mt-5 sm:mt-7 text-[28px] sm:text-[38px] md:text-[56px] lg:text-[68px] font-black leading-[1.05] tracking-[-2px] sm:tracking-[-3px] text-slate-900"
            >
              Trusted Industrial
              <span className="block bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">Supply Partner</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-5 sm:mt-7 text-slate-600 text-sm sm:text-base md:text-lg leading-[1.9] max-w-3xl mx-auto">
              Delivering reliable industrial procurement, electrical infrastructure products and telecom solutions with global sourcing expertise, competitive pricing and fast delivery support.
            </motion.p>
          </motion.div>

          {/* FEATURES GRID */}
          <motion.div
            className="mt-12 sm:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-7"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
          >
            {[
              { icon: "fa-globe", title: "Global Sourcing", desc: "Strong international supplier network with access to premium industrial brands worldwide." },
              { icon: "fa-shipping-fast", title: "Fast Delivery", desc: "Efficient procurement and logistics support ensuring timely project execution." },
              { icon: "fa-award", title: "Premium Quality", desc: "Supplying reliable products from globally trusted electrical and automation manufacturers." },
              { icon: "fa-headset", title: "Dedicated Support", desc: "Professional technical assistance and customer support for industrial requirements." },
            ].map((item) => (
              <motion.div
                key={item.title}
                variants={fadeUp}
                className="group bg-white rounded-[24px] sm:rounded-[32px] border border-blue-100 p-5 sm:p-8 shadow-[0_15px_45px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-[0_25px_60px_rgba(37,99,235,0.15)] active:scale-[0.98] transition-all duration-500"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-blue-700 to-cyan-500 text-white grid place-items-center text-lg sm:text-2xl shadow-xl group-hover:scale-110 transition duration-500">
                  <i className={`fas ${item.icon}`} />
                </div>
                <h3 className="mt-4 sm:mt-7 text-base sm:text-2xl font-bold text-slate-900">{item.title}</h3>
                <div className="w-10 sm:w-14 h-1 rounded-full bg-gradient-to-r from-blue-700 to-cyan-500 mt-3 sm:mt-5" />
                <p className="mt-3 sm:mt-6 text-slate-600 leading-[1.9] text-xs sm:text-[15px]">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* BOTTOM STATS */}
          <motion.div
            className="mt-10 sm:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            {[["15+", "Years Industry Experience"], ["100+", "Global Supply Brands"], ["500+", "Completed Deliveries"], ["24/7", "Customer Assistance"]].map(([num, label]) => (
              <motion.div
                key={label}
                variants={fadeUp}
                className="bg-white rounded-[24px] sm:rounded-[30px] border border-blue-100 p-5 sm:p-7 text-center shadow-lg hover:shadow-2xl hover:-translate-y-1 active:scale-95 transition-all duration-500"
              >
                <div className="text-3xl sm:text-4xl md:text-5xl font-black bg-gradient-to-r from-blue-700 to-cyan-500 bg-clip-text text-transparent">
                  {num}
                </div>
                <div className="mt-2 sm:mt-4 text-[10px] sm:text-[12px] uppercase tracking-[0.15em] sm:tracking-[0.22em] font-bold text-slate-500">
                  {label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ========================= */}
      {/* CONTACT */}
      {/* ========================= */}
      <section id="contact" className="scroll-mt-28 bg-gradient-to-b from-white to-blue-50 py-16 sm:py-24">
        <div className="max-w-[1450px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-10 sm:gap-14">

          {/* LEFT */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.span variants={fadeUp} className="bj-eyebrow">Contact Us</motion.span>
            <motion.h2 variants={fadeUp} className="mt-3 sm:mt-4 text-[28px] sm:text-[38px] md:text-[56px] font-black text-blue-950">
              Send Your Inquiry
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-4 sm:mt-6 text-slate-600 text-sm sm:text-base md:text-lg leading-[1.9] max-w-lg">
              Reach out to our team for industrial supply, telecom, electrical and power infrastructure requirements.
            </motion.p>

            <motion.div variants={stagger} className="mt-8 sm:mt-10 grid sm:grid-cols-2 gap-4 sm:gap-5">
              <motion.div variants={fadeUp} className="bg-white rounded-2xl sm:rounded-3xl border border-blue-100 p-4 sm:p-6 shadow-lg active:scale-95 transition-transform">
                <div className="font-bold text-blue-950 text-sm sm:text-lg">
                  <i className="fas fa-user-tie text-blue-600 mr-2" />
                  Zubair Bin Mohammed
                </div>
                <div className="text-slate-500 mt-1 text-xs sm:text-sm">Managing Director</div>
                <div className="mt-3 sm:mt-4 text-slate-700 leading-7 text-xs sm:text-sm">
                  zubair@bajabarinc.ae<br />+971 525131571
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="bg-white rounded-2xl sm:rounded-3xl border border-blue-100 p-4 sm:p-6 shadow-lg active:scale-95 transition-transform">
                <div className="font-bold text-blue-950 text-sm sm:text-lg">
                  <i className="fas fa-user-tie text-blue-600 mr-2" />
                  Mohammed A. Mannan
                </div>
                <div className="text-slate-500 mt-1 text-xs sm:text-sm">Business Development</div>
                <div className="mt-3 sm:mt-4 text-slate-700 leading-7 text-xs sm:text-sm">
                  mohammed@bajabarinc.ae<br />+91 81252 11886
                </div>
              </motion.div>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-8 sm:mt-10">
              <SocialRow />
            </motion.div>
          </motion.div>

          {/* FORM */}
          <motion.div
            className="bg-white rounded-[28px] sm:rounded-[40px] border border-blue-100 shadow-[0_25px_60px_rgba(0,0,0,0.08)] p-6 sm:p-8 md:p-10"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl sm:text-3xl font-black text-blue-950">Get In Touch</h3>
            <p className="mt-2 sm:mt-3 text-slate-600 text-sm sm:text-base">Fill the form and our team will contact you shortly.</p>
            <div className="mt-6 sm:mt-8">
              <ContactForm
                division="trading"
                services={["Electrical", "Lighting", "Telecom", "Power", "Vertiv Solutions", "Other"]}
              />
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}